import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});

document.addEventListener('DOMContentLoaded', () => {
  const observer = new MutationObserver(() => {
    const shopifyChat = document.querySelector('#ShopifyChat');

    if (shopifyChat && shopifyChat.shadowRoot) {
      const shadowRoot = shopifyChat.shadowRoot;

      // Check if the style is already injected
      if (!shadowRoot.querySelector('#hide-chat-notification')) {
        // Create a <style> element
        const style = document.createElement('style');
        style.id = 'hide-chat-notification';
        style.textContent = `
          .chat-notification {
            display: none !important;
          }
        `;

        // Append the style to the ShadowRoot
        shadowRoot.appendChild(style);
      }

      // Disconnect observer once the style is injected
      observer.disconnect();
    }
  });

  // Observe the DOM for changes
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
});





















document.querySelectorAll('.ymq-input-text').forEach((el, i) => {
  if (!el.getAttribute('aria-label')) {
    el.setAttribute('aria-label', 'Custom input field');
  }
});










document.addEventListener('DOMContentLoaded', () => {
  const tryLabelChat = () => {
    const shopifyChat = document.querySelector('#ShopifyChat');
    if (!shopifyChat?.shadowRoot) return false;

    const btn = shopifyChat.shadowRoot.querySelector('button[class*="chat-toggle"]');
    if (!btn) return false;

    btn.setAttribute('aria-label', 'Open live chat');
    return true;
  };

  let tries = 0;
  const maxTries = 20;

  const interval = setInterval(() => {
    tries += 1;
    const worked = tryLabelChat();

    if (worked || tries >= maxTries) {
      clearInterval(interval);
    }
  }, 500);
});












function setupJudgeMeReviewPics() {
  document.querySelectorAll('.jdgm-rev__pic-img').forEach((img) => {
    img.removeAttribute('tabindex');
    img.removeAttribute('role');

    const trigger =
      img.closest('a, button, .jdgm-rev__pic-link, .jdgm-rev__pic') || img.parentElement;

    if (!trigger) return;

    if (trigger.matches('a, button')) {
      return; // already keyboard-focusable
    }

    if (!trigger.dataset.jdgmKbBound) {
      trigger.dataset.jdgmKbBound = 'true';
      trigger.tabIndex = 0;
      trigger.setAttribute('role', 'button');

      if (!trigger.getAttribute('aria-label')) {
        trigger.setAttribute('aria-label', 'Open review image');
      }

      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          trigger.click();
        }
      });
    }
  });
}

document.addEventListener('DOMContentLoaded', setupJudgeMeReviewPics);

new MutationObserver(setupJudgeMeReviewPics).observe(document.body, {
  childList: true,
  subtree: true
});













function setupJudgeMeLoadMore() {
  const loadMore = document.querySelector('.jdgm-paginate__load-more.jdgm-btn.jdgm-btn--solid');
  if (!loadMore || loadMore.dataset.kbReady === 'true') return;

  loadMore.dataset.kbReady = 'true';

  if (!loadMore.matches('button, a[href]')) {
    loadMore.setAttribute('tabindex', '0');
    loadMore.setAttribute('role', 'button');
  }

  loadMore.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      loadMore.click();
    }
  });
}

document.addEventListener('DOMContentLoaded', setupJudgeMeLoadMore);

new MutationObserver(setupJudgeMeLoadMore).observe(document.body, {
  childList: true,
  subtree: true
});



































(function () {
  function ensureSmileRing() {
    let ring = document.getElementById('smile-keyboard-focus-ring');

    if (!ring) {
      ring = document.createElement('div');
      ring.id = 'smile-keyboard-focus-ring';
      document.body.appendChild(ring);
    }

    return ring;
  }

  function updateSmileRing() {
    const iframe = document.querySelector('#smile-lite-launcher-frame');
    const ring = ensureSmileRing();

    if (!iframe || document.activeElement !== iframe) {
      ring.style.display = 'none';
      return;
    }

    const rect = iframe.getBoundingClientRect();

    ring.style.display = 'block';
    ring.style.position = 'fixed';
    ring.style.left = `${rect.left}px`;
    ring.style.top = `${rect.top}px`;
    ring.style.width = `${rect.width}px`;
    ring.style.height = `${rect.height}px`;
  }

  function injectSmileRingCSS() {
    if (document.getElementById('smile-keyboard-focus-style')) return;

    const style = document.createElement('style');
    style.id = 'smile-keyboard-focus-style';
    style.textContent = `
      #smile-keyboard-focus-ring {
        display: none;
        pointer-events: none;
        z-index: 2147483647 !important;
        border: 3px solid #B18E86 !important;
        border-radius: 34px !important;
        box-shadow: 0 0 0 6px rgba(34, 0, 255, 1) !important;
        box-sizing: border-box !important;
      }
    `;

    document.head.appendChild(style);
  }

  function initSmileFocusRing() {
    injectSmileRingCSS();
    ensureSmileRing();
    setInterval(updateSmileRing, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSmileFocusRing);
  } else {
    initSmileFocusRing();
  }
})();




document.addEventListener('focusin', function () {
  const active = document.activeElement;

  if (!active || !active.classList.contains('jm-mfp-wrap')) return;

  const closeBtn = active.querySelector('.jm-mfp-close');
  if (closeBtn) {
    closeBtn.focus();
  }
});

























Object.defineProperty(HTMLElement.prototype, 'tabIndex', {
  set(value) {
    if (this.closest('.rebuy-product-price')) return;
    return Reflect.set(this, 'tabIndex', value);
  }
});
